import React from "react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ChatJessMessage, FileAttachment } from "./types";
import ChatJessAudioPlayer from "./components/ChatJessAudioPlayer";
import AdvancedDocumentPreview from "./components/AdvancedDocumentPreview";
import { downloadFile, isAgentProxyUrl } from "../../../services/process/api";
import { getFileIcon, getFileIconColor } from "../../../utils/fileIcons";
import { formatFileSize } from "../../../utils";
import { logger } from "../../../utils/logger";
import { questionMatchesAnyLanguage } from "../../../utils/translationUtils";
import styles from "./styles.module.scss";

interface Props {
  messages: ChatJessMessage[];
  onFeedbackClick: (msg: ChatJessMessage) => void;
  feedbackOpen: boolean;
  openFeedbackMessageId: string | null;
  isDarkTheme: boolean;
}

const ChatJessMessages: React.FC<Props> = ({
  messages,
  onFeedbackClick,
  feedbackOpen = false,
  openFeedbackMessageId = null,
  isDarkTheme = true,
}) => {
  const { t } = useTranslation();

  // Estado para controlar quais mensagens estão expandidas
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set(),
  );

  // Estado para controlar o preview de documentos
  const [previewAttachment, setPreviewAttachment] =
    useState<FileAttachment | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Configuração para truncar texto (similar ao WhatsApp)
  const MAX_TEXT_LENGTH = 300; // Caracteres antes de mostrar "Ler mais"
  const MAX_LINES = 8; // Linhas máximas antes de truncar

  const handleFileDownload = async (attachment: FileAttachment) => {
    if (!attachment.url) return;

    try {
      // PRIMEIRA PRIORIDADE: Verificar se é um tipo que pode fazer preview
      if (
        attachment.type === "application/pdf" ||
        attachment.type.startsWith("image/") ||
        (attachment.type.startsWith("text/") &&
          !attachment.name.toLowerCase().endsWith(".csv") &&
          !attachment.type.includes("csv"))
      ) {
        // Para PDFs, imagens e arquivos de texto (exceto CSV), abrir preview modal
        setPreviewAttachment(attachment);
        setIsPreviewOpen(true);
      } else if (isAgentProxyUrl(attachment.url)) {
        // Use special download for agent-proxy URLs (para tipos que não fazem preview)
        await downloadFile(attachment.url, attachment.name);
      } else {
        // Para outros documentos, baixar diretamente
        const link = document.createElement("a");
        link.href = attachment.url;
        link.download = attachment.name;
        link.click();
      }
    } catch (error) {
      logger.error("Erro ao baixar arquivo:", error);
      logger.error("Failed to download file:", error);
    }
  };

  const handleDirectDownload = async (attachment: FileAttachment) => {
    if (!attachment.url) return;

    try {
      if (isAgentProxyUrl(attachment.url)) {
        // Use special download for agent-proxy URLs
        await downloadFile(attachment.url, attachment.name);
      } else {
        const link = document.createElement("a");
        link.href = attachment.url;
        link.download = attachment.name;
        link.click();
      }
    } catch (error) {
      logger.error("Erro ao baixar arquivo:", error);
      logger.error("Failed to download file:", error);
    }
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewAttachment(null);
  };

  const renderAttachments = (attachments: FileAttachment[]) => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <div className={styles.messageAttachments}>
        {attachments.map((attachment, index) => {
          const isImage = attachment.type.startsWith("image/");

          if (isImage) {
            // Prefer the full URL over thumbnail for better quality
            const imageSource =
              attachment.url && !attachment.url.startsWith("data:")
                ? attachment.url
                : attachment.thumbnail || attachment.url;

            // Verificação específica se a imagem tem uma fonte válida
            if (!imageSource) {
              return null; // Não renderizar imagens sem fonte
            }

            return (
              <div
                key={attachment.id || index}
                className={styles.messageAttachmentImage}
              >
                <img
                  src={imageSource}
                  alt={attachment.name}
                  crossOrigin="use-credentials"
                  onClick={() => handleFileDownload(attachment)}
                  style={
                    {
                      cursor: "pointer",
                      imageRendering: "auto",
                      filter: "none",
                      objectPosition: "center",
                      objectFit: "contain",
                    } as React.CSSProperties
                  }
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;

                    if (
                      target.src !== attachment.thumbnail &&
                      attachment.thumbnail
                    ) {
                      target.src = attachment.thumbnail;
                    } else {
                      // Show broken image placeholder
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (
                        parent &&
                        !parent.querySelector(".broken-image-placeholder")
                      ) {
                        const placeholder = document.createElement("div");
                        placeholder.className = "broken-image-placeholder";
                        placeholder.style.cssText = `
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          min-height: 100px;
                          background: rgba(255, 255, 255, 0.1);
                          border-radius: 12px;
                          color: rgba(255, 255, 255, 0.7);
                          font-size: 14px;
                        `;
                        placeholder.innerHTML = `<i class="fas fa-image" style="margin-right: 8px;"></i>${t("chatjess.ui.imageNotAvailable")}`;
                        parent.appendChild(placeholder);
                      }
                    }
                  }}
                  onLoad={(e) => {
                    const target = e.target as HTMLImageElement;

                    // Remove any broken image placeholder
                    const parent = target.parentElement;
                    const placeholder = parent?.querySelector(
                      ".broken-image-placeholder",
                    );
                    if (placeholder) {
                      placeholder.remove();
                    }
                    target.style.display = "block";
                  }}
                />
                <div className={styles.imageOverlay}>
                  <button
                    className={styles.downloadButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDirectDownload(attachment);
                    }}
                  >
                    <i className="fas fa-download" />
                  </button>
                </div>
              </div>
            );
          } else {
            // Renderizar documentos e outros arquivos
            return (
              <div
                key={attachment.id || index}
                className={styles.messageAttachment}
                onClick={() => handleFileDownload(attachment)}
              >
                <div className={styles.messageAttachmentIcon}>
                  <i
                    className={getFileIcon(attachment.type, attachment.name)}
                    style={{
                      color: getFileIconColor(attachment.type, attachment.name),
                    }}
                  />
                </div>
                <div className={styles.messageAttachmentInfo}>
                  <div className={styles.messageAttachmentName}>
                    {attachment.name}
                  </div>
                  <div className={styles.messageAttachmentSize}>
                    {formatFileSize(attachment.size)}
                  </div>
                </div>
                <div className={styles.downloadIcon}>
                  <i
                    className={
                      attachment.type === "application/pdf" ||
                      (attachment.type.startsWith("text/") &&
                        !attachment.name.toLowerCase().endsWith(".csv") &&
                        attachment.type.includes("csv"))
                        ? "fas fa-eye"
                        : "fas fa-download"
                    }
                  />
                </div>
              </div>
            );
          }
        })}
      </div>
    );
  };

  // Função para verificar se o texto deve ser truncado
  const shouldTruncateText = (text: string): boolean => {
    if (!text) return false;
    const lines = text.split("\n");
    return text.length > MAX_TEXT_LENGTH || lines.length > MAX_LINES;
  };

  // Função para truncar o texto
  const truncateText = (text: string): string => {
    if (!text) return text;

    const lines = text.split("\n");

    // Se tem muitas linhas, truncar por linhas
    if (lines.length > MAX_LINES) {
      return lines.slice(0, MAX_LINES).join("\n");
    }

    // Se o texto é muito longo, truncar por caracteres
    if (text.length > MAX_TEXT_LENGTH) {
      // Encontrar o último espaço antes do limite para não cortar palavras
      let truncateAt = MAX_TEXT_LENGTH;
      const lastSpace = text.lastIndexOf(" ", MAX_TEXT_LENGTH);
      if (lastSpace > MAX_TEXT_LENGTH - 50) {
        // Se o espaço não está muito longe
        truncateAt = lastSpace;
      }
      return text.substring(0, truncateAt);
    }

    return text;
  };

  // Função para alternar expansão da mensagem
  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const getMessageContent = (msg: ChatJessMessage): string => {
    if (msg.isAudioMessage || msg.messageType === "audio") {
      return "";
    }

    const content = msg.content || msg.message || "";

    if (msg.supportType === "requiresHumanAnswer") {
      return t("chatjess.feedback.requiresHumanAnswerPrefix") + content;
    }
    if (msg.supportType === "requiresHumanAction") {
      return t("chatjess.feedback.requiresHumanActionPrefix") + content;
    }

    return content;
  };

  const formatMessageText = (text: string): React.ReactNode[] => {
    if (!text) return [];

    const lines = text.split("\n");
    const result: React.ReactNode[] = [];

    lines.forEach((line, lineIndex) => {
      if (lineIndex > 0) {
        result.push(<br key={`br-${lineIndex}`} />);
      }

      const parts = line.split(/(\*[^*]+\*)/g);

      parts.forEach((part, partIndex) => {
        if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
          const boldText = part.slice(1, -1);
          result.push(
            <strong key={`${lineIndex}-${partIndex}`}>{boldText}</strong>,
          );
        } else if (part) {
          result.push(part);
        }
      });
    });

    return result;
  };

  const getMessageClass = (msg: ChatJessMessage): string => {
    const isFromJess = msg.author === "jess" || msg.type === "jess";
    const baseClass = isFromJess ? styles.messageJess : styles.messageHuman;

    // Add special class for feedback_request messages
    if (msg.feedbackId && isFromJess) {
      return `${baseClass} ${styles.feedbackRequest}`;
    }

    // Add special class for audio messages
    if (msg.isAudioMessage || msg.messageType === "audio") {
      return `${baseClass} ${styles.audioMessage}`;
    }

    return baseClass;
  };

  // Verifica se todas as perguntas de uma mensagem foram respondidas
  const areAllQuestionsAnswered = (msg: ChatJessMessage): boolean => {
    if (!msg.supportMessages || msg.supportMessages.length === 0) {
      return msg.isCompleted || false;
    }
    return msg.supportMessages.every((supportMsg) => supportMsg.answer);
  };

  const getFeedbackButtonText = (msg: ChatJessMessage): string => {
    // Se o painel está aberto e é esta mensagem, mostrar "Observando"
    if (feedbackOpen && openFeedbackMessageId === msg.id) {
      return t("chatjess.ui.watching");
    }

    // Se todas as perguntas foram respondidas, mostrar "Ver Detalhes"
    if (areAllQuestionsAnswered(msg)) {
      return t("chatjess.ui.seeDetails");
    }

    const latestAnswer = msg.supportMessages?.[msg.supportMessages?.length - 1];
    if (
      questionMatchesAnyLanguage(latestAnswer?.question) &&
      latestAnswer?.answer === undefined
    ) {
      return t("chatjess.ui.seeDetails");
    }

    // Caso contrário, mostrar "Dar Feedback"
    return t("chatjess.ui.giveFeedback");
  };

  const getFeedbackButtonClass = (msg: ChatJessMessage): string => {
    const baseClass = styles.feedbackBtn;

    // Se o painel está aberto e é esta mensagem, usar classe "watching"
    if (feedbackOpen && openFeedbackMessageId === msg.id) {
      return `${baseClass} ${styles.watching}`;
    }

    // Se todas as perguntas foram respondidas ou tem respostas parciais, usar classe "seeDetailsBtn"
    if (
      areAllQuestionsAnswered(msg) ||
      (msg.supportMessages &&
        msg.supportMessages.some((supportMsg) => supportMsg.answer))
    ) {
      return `${baseClass} ${styles.seeDetailsBtn}`;
    }

    return baseClass;
  };

  const getFeedbackButtonIcon = (msg: ChatJessMessage): React.ReactNode => {
    // Se o painel está aberto e é esta mensagem, mostrar ícone de olho
    if (feedbackOpen && openFeedbackMessageId === msg.id) {
      return <i className="fas fa-eye" style={{ marginRight: "4px" }} />;
    }
    return null;
  };

  const renderMessageContent = (msg: ChatJessMessage) => {
    const hasAttachments = msg.attachments && msg.attachments.length > 0;

    // Se é mensagem de áudio, renderizar o player
    if (msg.isAudioMessage || msg.messageType === "audio") {
      if (msg.audioData) {
        return (
          <div className={styles.audioMessageContainer}>
            <ChatJessAudioPlayer
              audioUrl={msg.audioData.url}
              audioBlob={msg.audioData.blob}
              waveformData={msg.audioData.waveformData}
              isFromMe={msg.author === "human" || msg.type === "human"}
              isDarkTheme={isDarkTheme}
              duration={msg.audioData.duration}
            />
          </div>
        );
      } else {
        return (
          <div className={styles.messageContent}>
            <i className="fas fa-microphone" style={{ marginRight: "8px" }} />
            {t("chatjess.ui.audioMessage")}
          </div>
        );
      }
    }

    const attachmentsComponent = hasAttachments
      ? renderAttachments(msg.attachments!)
      : null;

    // Renderizar conteúdo de texto normal
    const content = getMessageContent(msg);
    let textComponent = null;

    if (content) {
      const shouldTruncate = shouldTruncateText(content);
      const isExpanded = expandedMessages.has(msg.id);
      const displayContent =
        shouldTruncate && !isExpanded ? truncateText(content) : content;

      textComponent = (
        <div className={styles.messageContent}>
          {formatMessageText(displayContent)}
          {shouldTruncate && (
            <button
              className={styles.readMoreBtn}
              onClick={() => toggleMessageExpansion(msg.id)}
              style={{ marginLeft: "4px" }}
            >
              {isExpanded
                ? t("chatjess.ui.readLess")
                : t("chatjess.ui.readMore")}
            </button>
          )}
        </div>
      );
    }

    // Se tem anexos e texto, retornar ambos
    if (attachmentsComponent && textComponent) {
      return (
        <>
          {attachmentsComponent}
          {textComponent}
        </>
      );
    }

    if (attachmentsComponent) {
      return attachmentsComponent;
    }

    return textComponent;
  };

  return (
    <>
      {messages.length === 0 ? (
        <div className={styles.welcomeContainer}>
          <div className={styles.welcomeMessage}>
            <div className={styles.jessWelcomeAvatar}>
              <div className={styles.jessAvatar}>J</div>
            </div>
            <div className={styles.welcomeText}>
              {t("chatjess.ui.welcomeMessage")}
            </div>
          </div>
        </div>
      ) : (
        messages.map((msg) => (
          <div key={msg.id} className={getMessageClass(msg)}>
            {(msg.author === "human" || msg.type === "human") &&
              msg.userName && (
                <div className={styles.userName}>{msg.userName}</div>
              )}
            {msg.feedbackId && (
              <button
                className={getFeedbackButtonClass(msg)}
                onClick={() => onFeedbackClick(msg)}
              >
                {getFeedbackButtonIcon(msg)}
                {getFeedbackButtonText(msg)}
              </button>
            )}
            {renderMessageContent(msg)}
          </div>
        ))
      )}

      {/* Advanced Document Preview Modal */}
      {previewAttachment && (
        <AdvancedDocumentPreview
          attachment={previewAttachment}
          isOpen={isPreviewOpen}
          onClose={closePreview}
          isDarkTheme={isDarkTheme}
        />
      )}
    </>
  );
};

export default ChatJessMessages;
